import { DynamicSupplyHardware } from "@/types";
import { useMemo } from "react";
import { useHardwareTagNames } from "../../hooks/useHardwareTagNames";

export type HardwareSortOption =
  | "recommended"
  | "price_asc"
  | "price_desc"
  | "vram_asc"
  | "vram_desc";

const num = (n: unknown) => (typeof n === "number" && !Number.isNaN(n) ? n : 0);

const compareBySort = (
  sortValue: HardwareSortOption,
  a: DynamicSupplyHardware,
  b: DynamicSupplyHardware
) => {
  if (sortValue === "price_asc") return num(a.price) - num(b.price);
  if (sortValue === "price_desc") return num(b.price) - num(a.price);
  if (sortValue === "vram_asc") return num(a.vramPerCard) - num(b.vramPerCard);
  if (sortValue === "vram_desc") return num(b.vramPerCard) - num(a.vramPerCard);
  return 0;
};

const getRecommendedTagPriority = (
  hardwareName: string,
  recommendedHardwareNames: string[],
  mostDeployedHardwareNames: string[]
) => {
  const normalizedHardwareName = hardwareName.toLowerCase();
  if (recommendedHardwareNames.includes(normalizedHardwareName)) return 0;
  if (mostDeployedHardwareNames.includes(normalizedHardwareName)) return 1;
  return 2;
};

export function sortHardwareList<T extends DynamicSupplyHardware>(
  items: T[],
  sortValue: HardwareSortOption,
  isHardwareTagsEnabled: boolean,
  recommendedHardwareNames: string[],
  mostDeployedHardwareNames: string[]
): T[] {
  const withIndex = items.map((h, idx) => ({ h, idx }));
  if (sortValue === "recommended") {
    if (!isHardwareTagsEnabled) return items;
    withIndex.sort((a, b) => {
      const priorityDiff =
        getRecommendedTagPriority(
          a.h.hardwareName,
          recommendedHardwareNames,
          mostDeployedHardwareNames
        ) -
        getRecommendedTagPriority(
          b.h.hardwareName,
          recommendedHardwareNames,
          mostDeployedHardwareNames
        );
      return priorityDiff !== 0 ? priorityDiff : a.idx - b.idx;
    });
    return withIndex.map((x) => x.h);
  }
  withIndex.sort((a, b) => {
    const primary = compareBySort(sortValue, a.h, b.h);
    return primary !== 0 ? primary : a.idx - b.idx;
  });
  return withIndex.map((x) => x.h);
}

export function useSort<T extends DynamicSupplyHardware>(
  items: T[],
  sortValue: HardwareSortOption = "recommended"
) {
  const { isHardwareTagsEnabled, recommendedHardwareNames, mostDeployedHardwareNames } =
    useHardwareTagNames();

  const sortedItems = useMemo(
    () =>
      sortHardwareList(
        items,
        sortValue,
        isHardwareTagsEnabled,
        recommendedHardwareNames,
        mostDeployedHardwareNames
      ),
    [
      items,
      sortValue,
      isHardwareTagsEnabled,
      recommendedHardwareNames,
      mostDeployedHardwareNames
    ]
  );

  return { sortedItems, isSortingEnabled: true as const };
}
